import React, { useEffect, useMemo, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';

import { CardBody } from '../../../bootstrap/Card';

import { Token } from '../../../../type/token';

import { TableColumn } from '../table-components/table-column';
import { TokenRow } from './token-row';
import classNames from 'classnames';
import useDarkMode from '../../../../hooks/useDarkMode';
import { TOKEN_STATUS } from '../../token/token-activation-form/blocks/constants';
import Icon from '../../../icon/Icon';

export const getUniqueInviteTokens = (tokens: Array<Token>) => {
	const uniqueList: Array<Token> = [];

	for (let i = 0; i < tokens.length; i += 1) {
		if (
			!uniqueList.some((token) => token.id === tokens[i].id && token.authorizationType === 'invite')
		) {
			uniqueList.push(tokens[i]);
		}
	}
	return uniqueList;
};

interface TokensTableProps {
	tokens: Array<Token> | undefined;
	isFluid: any;
	handleUpcomingEdit: any;
	setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
	setShouldOpenEditUserForm: React.Dispatch<React.SetStateAction<boolean>>;
	setShouldOpenSeeAccess: React.Dispatch<React.SetStateAction<boolean>>;
	setUserToEdit: React.Dispatch<React.SetStateAction<number>>;
	setTokenToEdit: React.Dispatch<React.SetStateAction<Token | null>>;
	setShouldOpenEditTokenForm: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TokensTable: React.FC<TokensTableProps> = ({
	tokens,
	isFluid,
	handleUpcomingEdit,
	setRefreshTable,
	setShouldOpenEditUserForm,
	setShouldOpenSeeAccess,
	setUserToEdit,
	setShouldOpenEditTokenForm,
	setTokenToEdit
}) => {
	const { darkModeStatus } = useDarkMode();
	const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());

	const groupedTokens: Map<number, Token[]> = useMemo(() => {
		if (!tokens) return new Map();
		
		const groups = new Map<number, Token[]>();
		tokens.forEach(token => {
			const userId = token.userRequested.id;
			if (!groups.has(userId)) {
				groups.set(userId, []);
			}
			groups.get(userId)!.push(token);
		});
		return groups;
	}, [tokens]);

	const toggleUserExpansion = (userId: number) => {
		setExpandedUsers(prev => {
			const newSet = new Set(prev);
			if (newSet.has(userId)) {
				newSet.delete(userId);
			} else {
				newSet.add(userId);
			}
			return newSet;
		});
	};

	if (_isEmpty(tokens)) return (
		<CardBody className='table-responsive' isScrollable={isFluid} style={{ height: '40rem' }}>
			<table className='table table-modern table-responsive'>
				<thead>
					<tr key={'user-token-tables-names'}>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}></TableColumn>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}>Qtd. Autorizações</TableColumn>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}>Nome do Usuário</TableColumn>
					</tr>
				</thead>
				<tbody>
					<tr style={{ textAlign: 'center' }}>
						<td colSpan={3}>Nenhum acesso realizado ainda.</td>
					</tr>
				</tbody>
			</table>
		</CardBody>
	);

	return (
		<CardBody className='table-responsive' isScrollable={isFluid} style={{ height: '40rem' }}>
			<table className='table table-modern table-responsive'>
				<thead>
					<tr key={'user-token-tables-names'}>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}></TableColumn>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}>Nome do Usuário</TableColumn>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}>Qtd. Autorizações</TableColumn>
						<TableColumn style={{ fontWeight: 'bold', fontSize: '16px' }}>Autorizações Ativas</TableColumn>
					</tr>
				</thead>
				<tbody>
					{Array.from(groupedTokens.entries()).map(([userId, userTokens]) => {
						const isExpanded = expandedUsers.has(userId);
						const firstToken = userTokens[0];
						
						return (
							<React.Fragment key={`user-group-${userId}`}>
								<tr 
									style={{ 
										cursor: 'pointer',
										backgroundColor: darkModeStatus ? '#2a2a2a' : '#f8f9fa',
										fontWeight: 'bold'
									}}
									onClick={() => toggleUserExpansion(userId)}
								>
									<TableColumn>
										<Icon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
									</TableColumn>
									<TableColumn>
										{firstToken.userRequested.name}
									</TableColumn>
									<TableColumn>
										<span>{userTokens.length}</span>
									</TableColumn>
									<TableColumn>
										<span>
											{userTokens.filter(token => token.status === TOKEN_STATUS.ACTIVE).length}
										</span>
									</TableColumn>
								</tr>
								{isExpanded && (
									<>
										<tr style={{ backgroundColor: darkModeStatus ? '#1e1e1e' : '#f0f0f0' }}>
											<TableColumn></TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Ações</TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Situação acesso</TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Nome</TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Autorizado por</TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Motivo</TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Data início</TableColumn>
											<TableColumn style={{ fontWeight: 'bold', fontSize: '14px' }}>Data fim</TableColumn>
										</tr>
										{userTokens.map((token) => (
											<TokenRow
												key={`token-row-${token.id}`}
												otherTokens={userTokens}
												setShouldOpenEditUserForm={setShouldOpenEditUserForm}
												setShouldOpenSeeAccess={setShouldOpenSeeAccess}
												setUserToEdit={setUserToEdit}
												token={token}
												setRefreshTable={setRefreshTable}
												handleUpcomingEdit={handleUpcomingEdit}
												setShouldOpenEditTokenForm={setShouldOpenEditTokenForm}
												setTokenToEdit={setTokenToEdit}
												isGrouped={true}
											/>
										))}
									</>
								)}
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</CardBody>
	);
};
